/**
 * The Reva context object and its consumer hook, deliberately separate from the
 * provider in RevaContext.tsx.
 *
 * The provider lazy-loads RevaPanel, which imports the app's full route table
 * and, through the admin pages, Node-only packages. Anything importing `useReva`
 * therefore dragged that whole graph in. That was harmless while only app code
 * used it, but PropertyMap now reads a shell offset from this hook, and
 * PropertyMap is consumed by the marketing Next.js build — which then failed on
 * `Can't resolve 'worker_threads'`.
 *
 * Keeping the context and hook here lets a consumer take the value without
 * pulling in the panel.
 */

import { createContext, useContext } from 'react';
import type { RevaBriefing } from '../api/services/revaBriefing';

export interface OpenRevaOptions {
  /** Question to send as soon as the chat opens. */
  question?: string;
}

export interface RevaContextValue {
  isOpen: boolean;
  /** False for accounts Reva must never serve (the counterparty on a seller listing). */
  available: boolean;
  openReva: (options?: OpenRevaOptions) => void;
  closeReva: () => void;
  briefing: RevaBriefing | null;
  briefingLoading: boolean;
  /** Re-fetch the briefing if it is older than a minute (or always when force=true). */
  refreshBriefing: (force?: boolean) => Promise<void>;
  /** Replies that landed while the panel was closed or collapsed, for the entry-point badges. */
  unseenCount: number;
  /** Called by the panel when a reply arrives; only counts if the panel is not open. */
  notifyReply: () => void;
}

export const RevaContext = createContext<RevaContextValue | null>(null);

/**
 * The context value when a provider is present, or null when it is not.
 *
 * Reva is app-only, but PropertyMap is exported to the marketing site, which
 * renders it with no RevaProvider (and no AuthProvider). A throwing hook is
 * right for app code that genuinely requires the provider and wrong for a
 * shared component, so public consumers take this and fall back.
 */
export const useRevaOptional = (): RevaContextValue | null => useContext(RevaContext);

export const useReva = (): RevaContextValue => {
  const ctx = useContext(RevaContext);
  if (!ctx) {
    throw new Error('useReva must be used within a RevaProvider');
  }
  return ctx;
};
